import React, { useEffect, useState } from 'react';
import { Inbox, Send, CheckCircle2, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';

function MessageRow({ msg, onReplied }) {
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);

  const sendReply = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/support/${msg.msg_id}/reply`, { reply });
      toast.success(`Reply emailed to ${msg.email}`);
      onReplied(msg.msg_id, reply);
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className={`border rounded-2xl transition-colors ${msg.status === 'replied' ? 'border-white/5' : 'border-boosta-cyan/25'}`} data-testid={`support-msg-${msg.msg_id}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-4 px-5 py-4 text-left" data-testid={`support-msg-toggle-${msg.msg_id}`}>
        <span className={`w-2 h-2 rounded-full shrink-0 ${msg.status === 'replied' ? 'bg-boosta-emerald' : 'bg-boosta-yellow pulse-glow'}`} />
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{msg.name} <span className="text-white/40 text-xs">· {msg.email}</span></div>
          <div className="text-xs text-white/50 truncate mt-0.5">{msg.message}</div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider font-bold shrink-0 ${msg.status === 'replied' ? 'text-boosta-emerald' : 'text-boosta-yellow'}`}>{msg.status}</span>
        <span className="text-xs text-white/35 shrink-0 hidden sm:block">{new Date(msg.created_at).toLocaleDateString()}</span>
        <ChevronDown className={`w-4 h-4 text-white/40 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-5 pb-5 space-y-4">
              <p className="text-sm text-white/70 leading-relaxed whitespace-pre-wrap border-l-2 border-boosta-cyan/40 pl-4">{msg.message}</p>
              {msg.status === 'replied' ? (
                <div className="text-sm text-white/50 flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-boosta-emerald mt-0.5 shrink-0" />
                  <span className="whitespace-pre-wrap">Replied {msg.replied_at ? new Date(msg.replied_at).toLocaleString() : ''}: {msg.reply}</span>
                </div>
              ) : (
                <div className="space-y-3">
                  <textarea rows={3} value={reply} onChange={e => setReply(e.target.value)} placeholder={`Write your reply to ${msg.name}…`}
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-boosta-cyan/60 outline-none resize-none"
                    data-testid={`support-reply-input-${msg.msg_id}`} />
                  <button onClick={sendReply} disabled={busy || reply.trim().length < 2}
                    className="btn-primary rounded-xl px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-40"
                    data-testid={`support-reply-send-${msg.msg_id}`}>
                    <Send className="w-3.5 h-3.5" /> {busy ? 'Sending…' : 'Send reply'}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function SupportInbox() {
  const [msgs, setMsgs] = useState(null);

  useEffect(() => {
    api.get('/admin/support').then(r => setMsgs(r.data)).catch(() => setMsgs([]));
  }, []);

  const onReplied = (msgId, reply) => setMsgs(m => m.map(x => x.msg_id === msgId
    ? { ...x, status: 'replied', reply, replied_at: new Date().toISOString() } : x));

  const openCount = (msgs || []).filter(m => m.status !== 'replied').length;

  return (
    <div className="space-y-8" data-testid="support-inbox-root">
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-2">Admin</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Support Inbox.</h1>
        <p className="text-white/50 text-sm mt-1">{msgs === null ? 'Loading…' : `${openCount} open · ${msgs.length} total — replies are emailed directly to the sender.`}</p>
      </div>
      {msgs !== null && msgs.length === 0 ? (
        <div className="glass rounded-2xl py-16 text-center text-white/45">
          <Inbox className="w-10 h-10 mx-auto mb-3 text-white/20" />
          <div className="text-sm">No support messages yet.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {(msgs || []).map(m => <MessageRow key={m.msg_id} msg={m} onReplied={onReplied} />)}
        </div>
      )}
    </div>
  );
}
